import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import useQrCodePayments from "../hooks/useQrCodePayments";
import { formatINR } from "../utils/formatINR";

const QrCodePayments = () => {
  const { payments, isLoading, error, message, refetch } = useQrCodePayments();
  const [localPayments, setLocalPayments] = useState([]);

  // Show server message as toast
  useEffect(() => {
    if (message) toast.success(message);
  }, [message]);

  // Clean and parse data
  useEffect(() => {
    if (payments) {
      const cleaned = payments.map(p => {
        let upiId = "";
        if (p.upi_details) {
          try {
            const parsed = JSON.parse(p.upi_details);
            upiId = parsed.vpa || "";
          } catch (err) {
            upiId = "";
          }
        }
  
        return {
          id: p.payment_id || "",
          vehicle_id: p.vehicle_id || "",
          gun_id: p.gun_id || "",
          amount: p.amount || 0,
          status: p.status || "",
          created_at: p.created_at ? new Date(p.created_at).toLocaleString() : "",
          description: p.description?.String || "",
          payment_reference_id: p.payment_reference_id?.String || "",
          station_id: p.station_id || "",         // <-- take directly
          station_name: p.station_name || "",     // <-- add this
          upi_id: upiId,
        };
      });
      setLocalPayments(cleaned);
    }
  }, [payments]);
  

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-xl font-semibold text-gray-900">
              QR Code Payments
            </h1>
            <button
              onClick={refetch}
              className="px-4 py-2 text-sm font-medium bg-black text-white rounded-lg hover:bg-gray-800"
            >
              Refresh
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {isLoading && <p className="text-gray-500">Loading payments...</p>}
        {error && <p className="text-red-500">{error}</p>}
        {message && <p className="text-gray-600">{message}</p>}

        <div className="space-y-4">
          {localPayments.length === 0 && !isLoading && (
            <p className="text-gray-500">No payments found.</p>
          )}

          {localPayments.map((p, idx) => (
            <div
              key={idx}
              className="bg-white shadow rounded-xl p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center border"
            >
              {/* Left info */}
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium text-gray-900">
                  Payment ID: <span className="text-gray-700">{p.id}</span>
                </p>
                <p className="text-sm font-medium text-gray-900">
                  Vehicle ID: <span className="text-gray-700">{p.vehicle_id}</span>
                </p>
                <p className="text-sm font-medium text-gray-900">
                  Gun ID: <span className="text-gray-700">{p.gun_id}</span>
                </p>
                <p className="text-sm font-medium text-gray-900">
                  Station ID: <span className="text-gray-700">{p.station_id}</span>
                </p>
                {p.upi_id && (
                  <p className="text-sm font-medium text-gray-900">
                    UPI ID: <span className="text-gray-700">{p.upi_id}</span>
                  </p>
                )}
                {p.description && (
                  <p className="text-sm text-gray-600">Description: {p.description}</p>
                )}
              </div>

              {/* Right info */}
              <div className="mt-2 sm:mt-0 text-sm text-gray-700 text-right space-y-1">
              <p className="text-gray-500 text-xs">Station Name: {p.station_name}</p>
                <p className="font-medium">Amount: {formatINR(p.amount)}</p>
                <p>
                  Status:{" "}
                  <span
                    className={`px-2 py-0.5 rounded text-xs ${
                      p.status === "completed"
                        ? "bg-green-100 text-green-700"
                        : p.status === "captured"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {p.status}
                  </span>
                </p>
                <p className="text-gray-500 text-xs">{p.created_at}</p>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default QrCodePayments;
